// ═══════════════════════════════════════════════════════════════════
// PremiumProductCard (Apr28 v6 + v8 layout) — the showpiece
// ═══════════════════════════════════════════════════════════════════
// Single source of truth for product cards across Shop, CategoryRail,
// and JustArrivedShowcase. Premium magazine-grade with:
//
//   • Portrait 4:5 image area (clean — no UI chrome overlaying photo)
//   • Gold gradient title in Georgia serif
//   • Gold radial halo + image scale 1.06x on hover
//   • Wishlist heart (22px) — small + glassmorphic, top-right of image
//   • Discount badge — bold red gradient pill, top-left of image
//   • "✦ NEW" gold ribbon for products <14 days old (stacks below discount)
//   • Stock urgency overlay ("Only 2 left ⚡" pulsing dot) when stock<5
//   • Sparkle decorations (✦/✧) floating on image during hover
//   • Image dots indicator (multi-image products)
//
//   • Info panel below image:
//     • Category caps + star rating
//     • Product name (Georgia serif, 2-line clamp)
//     • Price + struck MRP + green "Save ₹X" pill
//     • Quick Add button — gold gradient, FULL WIDTH, below price row
//       (moved from image overlay in v8 — keeps photo uncluttered)
//
// Variants:
//   variant="standard"  — used by Shop grid + CategoryRail
//   variant="showcase"  — slightly bigger fonts/padding (JustArrivedShowcase)
//
// All images go through cldCard() for Cloudinary auto-optimization.
// All hover effects desktop-only (mobile is touch — Quick Add always tappable
// since it's a real button now, not a hover-revealed overlay).
// ═══════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { cldCard } from '../../utils/cloudinary';
import { useCartStore } from '../../stores/cartStore';
import AffiliateLinkBuilder from '../AffiliateLinkBuilder';

const API = process.env.REACT_APP_API_URL || 'https://api.glitzandglambyjyoti.com';

const PremiumProductCard = ({
  product: p,
  hi = false,
  variant = 'standard',     // 'standard' | 'showcase'
  width,                     // optional fixed width (used by rail to make card sizing predictable)
}) => {
  const [hovered, setHovered] = useState(false);
  const [imgIdx, setImgIdx]   = useState(0);
  const [wished, setWished]   = useState(false);
  const [adding, setAdding]   = useState(false);
  // Apr28 v11 — creator state read from sessionStorage cache
  const [creatorCode, setCreatorCode] = useState(null);
  const [shareOpen, setShareOpen]     = useState(false);
  // Apr28 v15 — mobile detection so the floating 🔗 share icon (which
  // crowds the small image area) can move to the info panel on phones.
  // Heart stays floating because it needs to be one-tap visible for
  // every customer. Share is creator-only so it can be less prominent.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener('change', update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else mq.removeListener(update);
    };
  }, []);

  const navigate = useNavigate();
  const addToCart = useCartStore(s => s.addItem);

  const isShowcase = variant === 'showcase';

  // Pricing math (matches v1 cart-fix logic — never use raw .price for display)
  const sellPrice = p.discountPrice && p.discountPrice < p.price ? p.discountPrice : p.price;
  const discount  = p.discountPrice && p.discountPrice < p.price
    ? Math.round((1 - p.discountPrice / p.price) * 100) : 0;
  const savings   = discount > 0 ? p.price - sellPrice : 0;

  const isSoldOut = p.status === 'soldout' || p.stock === 0;
  const isLowStock = !isSoldOut && p.stock > 0 && p.stock < 5;

  // "NEW" ribbon — show for products created in the last 14 days
  const ageMs = p.createdAt ? Date.now() - new Date(p.createdAt).getTime() : null;
  const isFresh = ageMs !== null && ageMs < 1000 * 60 * 60 * 24 * 14;

  const hasMultipleImages = Array.isArray(p.images) && p.images.length > 1;
  const currentImg = p.images?.[imgIdx] || p.images?.[0];

  // ─── Sync wishlist state on mount ─────────────────────────────
  // Lightweight: read from sessionStorage cache populated by parent or first
  // wishlist API call. Cards don't each ping the API on render.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('gg_wishlist_ids');
      if (raw) {
        const ids = JSON.parse(raw);
        setWished(Array.isArray(ids) && ids.includes(p._id));
      }
    } catch (_) {}
  }, [p._id]);

  // Apr28 v11 — read creator status from sessionStorage (set by App.js).
  // If approved creator, show "🔗 Share with my code" button.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('gg_creator');
      if (raw) {
        const data = JSON.parse(raw);
        if (data?.code) setCreatorCode(data.code);
      }
    } catch (_) {}
  }, []);

  // ─── Handlers ──────────────────────────────────────────────────
  const handleQuickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSoldOut || adding) return;

    // Skip Quick Add for products WITH size or color variants — for those,
    // sending the user to the detail page is the safer flow.
    const hasVariants = (p.sizes?.length > 0) || (p.colors?.length > 0);
    if (hasVariants) {
      navigate(`/product/${p._id}`);
      return;
    }

    setAdding(true);
    addToCart({
      _id:        p._id,
      name:       p.name,
      price:      sellPrice,                // selling price, not MRP
      image:      p.images?.[0] || null,
      barcodeId:  p.barcodeId,
      size:       '',
      color:      '',
    });
    toast.success(
      hi
        ? `✨ ${p.name?.slice(0, 28)}${p.name?.length > 28 ? '…' : ''} कार्ट में जोड़ा गया!`
        : `✨ Added ${p.name?.slice(0, 28)}${p.name?.length > 28 ? '…' : ''} to cart!`
    );
    setTimeout(() => setAdding(false), 800);
  };

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const token = localStorage.getItem('token');
    if (!token) {
      toast.info(hi ? 'विशलिस्ट में सहेजने के लिए लॉगिन करें' : 'Log in to save to wishlist');
      navigate('/login?redirect=' + encodeURIComponent(window.location.pathname));
      return;
    }
    // Optimistic toggle
    const next = !wished;
    setWished(next);
    try {
      if (next) {
        await fetch(`${API}/api/wishlist/add`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body:    JSON.stringify({ productId: p._id }),
        });
        // Update cache
        try {
          const raw = sessionStorage.getItem('gg_wishlist_ids');
          const ids = raw ? JSON.parse(raw) : [];
          if (!ids.includes(p._id)) ids.push(p._id);
          sessionStorage.setItem('gg_wishlist_ids', JSON.stringify(ids));
        } catch (_) {}
        toast.success(hi ? '❤️ विशलिस्ट में सहेजा' : '❤️ Saved to wishlist');
      } else {
        await fetch(`${API}/api/wishlist/remove/${p._id}`, {
          method:  'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        try {
          const raw = sessionStorage.getItem('gg_wishlist_ids');
          const ids = raw ? JSON.parse(raw) : [];
          const filtered = ids.filter(id => id !== p._id);
          sessionStorage.setItem('gg_wishlist_ids', JSON.stringify(filtered));
        } catch (_) {}
      }
    } catch (err) {
      // Revert on error
      setWished(!next);
      toast.error(hi ? 'कुछ गलत हुआ' : 'Something went wrong');
    }
  };

  // Apr28 v11 — open AffiliateLinkBuilder modal pre-loaded with this product.
  // Only renders the button when user is an approved creator (creatorCode set).
  const handleShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShareOpen(true);
  };

  // Image cycle on hover (for products with multiple images)
  useEffect(() => {
    if (!hovered || !hasMultipleImages) {
      setImgIdx(0);
      return;
    }
    const id = setInterval(() => {
      setImgIdx(i => (i + 1) % Math.min(p.images.length, 4));
    }, 1100);
    return () => clearInterval(id);
  }, [hovered, hasMultipleImages, p.images?.length]);

  // ─── Styles (closures over isShowcase + hovered) ─────────────
  const cardStyle = {
    background: 'linear-gradient(180deg, #0e0b18 0%, #100d1f 100%)',
    border: hovered
      ? '1px solid rgba(201,168,76,0.55)'
      : '1px solid rgba(201,168,76,0.18)',
    borderRadius: 18,
    overflow: 'hidden',
    transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.35s ease, border-color 0.3s ease',
    transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
    boxShadow: hovered
      ? '0 22px 50px rgba(0,0,0,0.6), 0 0 0 1px rgba(201,168,76,0.25), 0 0 30px rgba(201,168,76,0.15)'
      : '0 6px 22px rgba(0,0,0,0.4)',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    opacity: isSoldOut ? 0.78 : 1,
    width: width || undefined,
  };

  const imgWrapStyle = {
    aspectRatio: '4 / 5',
    background: 'linear-gradient(135deg, #2a1f1a 0%, #1a1424 100%)',
    position: 'relative',
    overflow: 'hidden',
  };

  const imgStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transform: hovered ? 'scale(1.06)' : 'scale(1)',
    transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  const placeholderStyle = {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: isShowcase ? 64 : 56,
    opacity: 0.85,
    filter: 'drop-shadow(0 4px 14px rgba(201,168,76,0.4))',
  };

  // Gold radial halo on hover
  const haloStyle = {
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(ellipse at center, rgba(201,168,76,0.12) 0%, transparent 60%)',
    pointerEvents: 'none',
    opacity: hovered ? 1 : 0,
    transition: 'opacity 0.4s ease',
  };

  // Top shimmer line (1px gold gradient)
  const shimmerStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 1,
    background: hovered
      ? 'linear-gradient(90deg, transparent, rgba(240,208,128,0.9), transparent)'
      : 'linear-gradient(90deg, transparent, rgba(201,168,76,0.5), transparent)',
    transition: 'background 0.4s ease',
    pointerEvents: 'none',
  };

  // Bottom darken gradient — lighter now that Quick Add isn't sitting on it.
  // Only shows when stock urgency badge is present (needs contrast).
  const bottomGradientStyle = {
    position: 'absolute',
    bottom: 0, left: 0, right: 0,
    height: '35%',
    background: isLowStock
      ? 'linear-gradient(to top, rgba(10,8,16,0.85) 0%, transparent 100%)'
      : 'linear-gradient(to top, rgba(10,8,16,0.4) 0%, transparent 100%)',
    pointerEvents: 'none',
    transition: 'background 0.3s ease',
  };

  const discountBadgeStyle = {
    position: 'absolute',
    top: 8,
    left: 8,
    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
    color: '#fff',
    fontSize: isShowcase ? 10 : 9,
    fontWeight: 800,
    padding: '3px 8px',
    borderRadius: 999,
    fontFamily: 'system-ui',
    letterSpacing: 0.5,
    boxShadow: '0 3px 10px rgba(239,68,68,0.5)',
    zIndex: 3,
  };

  const newRibbonStyle = {
    position: 'absolute',
    // Apr28 v8: stack below discount badge with proper gap (was hardcoded 40)
    top: discount > 0 ? 36 : 8,
    left: 8,
    background: 'linear-gradient(135deg, #c9a84c, #f0d080)',
    color: '#04020a',
    fontSize: 8,
    fontWeight: 800,
    padding: '3px 8px',
    borderRadius: 999,
    fontFamily: 'system-ui',
    letterSpacing: 1.2,
    boxShadow: '0 2px 8px rgba(201,168,76,0.5)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    zIndex: 3,
  };

  const wishlistStyle = {
    position: 'absolute',
    top: 8,
    right: 8,
    // Apr28 v8: smaller on mobile (22px), still visible on desktop (26px)
    // — was 28px which dominated the image on small screens
    width:  22,
    height: 22,
    background: wished
      ? 'linear-gradient(135deg, #ef4444, #f87171)'
      : 'rgba(0,0,0,0.55)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    border: wished
      ? '1px solid rgba(239,68,68,0.4)'
      : '1px solid rgba(255,255,255,0.25)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 11,
    cursor: 'pointer',
    transition: 'transform 0.18s ease, background 0.25s ease',
    transform: wished ? 'scale(1.08)' : 'scale(1)',
    boxShadow: wished ? '0 2px 10px rgba(239,68,68,0.5)' : 'none',
    zIndex: 3,
    lineHeight: 1,
    padding: 0,
  };

  // Apr28 v11 — share button (creators only) — sits below wishlist heart
  // Same glassmorphic style, gold accent to distinguish purpose
  const shareBtnStyle = {
    position: 'absolute',
    top: 36,            // below the heart (heart top:8 + height:22 + 6 gap)
    right: 8,
    width: 22,
    height: 22,
    background: 'linear-gradient(135deg, rgba(201,168,76,0.95), rgba(240,208,128,0.95))',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#04020a',
    fontSize: 10,
    cursor: 'pointer',
    transition: 'transform 0.18s ease',
    boxShadow: '0 2px 8px rgba(201,168,76,0.45)',
    zIndex: 3,
    lineHeight: 1,
    padding: 0,
  };

  const stockUrgencyStyle = {
    position: 'absolute',
    // Apr28 v8: was bottom:44 to clear old Quick Add, now bottom:10
    // since Quick Add moved into info panel
    bottom: 10,
    left: 10,
    right: 10,
    background: 'rgba(0,0,0,0.75)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    border: '1px solid rgba(252,211,77,0.45)',
    color: '#fcd34d',
    fontSize: 10,
    fontWeight: 700,
    padding: '4px 9px',
    borderRadius: 8,
    fontFamily: 'system-ui',
    letterSpacing: 0.5,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    zIndex: 3,
  };

  const stockDotStyle = {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#fcd34d',
    boxShadow: '0 0 8px #fcd34d',
    animation: 'ggStockPulse 1.4s ease-in-out infinite',
  };

  // Apr28 v15 — Mobile-only inline share text-link below Quick Add.
  // Subtle, doesn't crowd the image, only visible to logged-in creators.
  const mobileShareLinkStyle = {
    width: '100%',
    background: 'rgba(201,168,76,0.06)',
    color: '#c9a84c',
    border: '1px dashed rgba(201,168,76,0.35)',
    borderRadius: 6,
    padding: '6px 0',
    marginTop: 6,
    fontSize: 11,
    fontWeight: 700,
    fontFamily: 'system-ui',
    letterSpacing: 0.5,
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'background 0.18s ease',
  };

  // Apr28 v8: Quick Add moved OUT of image overlay, now sits in info panel
  // below the price row. Smaller, less intrusive — image stays clean.
  // Disabled state when sold out, gold gradient when available.
  const quickAddStyle = {
    width: '100%',
    background: isSoldOut
      ? 'rgba(60,60,72,0.5)'
      : 'linear-gradient(135deg, rgba(201,168,76,0.95), rgba(240,208,128,0.95))',
    color: isSoldOut ? '#888' : '#04020a',
    fontSize: 10,
    fontWeight: 800,
    padding: '7px 0',
    borderRadius: 6,
    border: 'none',
    cursor: isSoldOut ? 'not-allowed' : 'pointer',
    fontFamily: 'system-ui',
    letterSpacing: 1,
    textTransform: 'uppercase',
    boxShadow: isSoldOut ? 'none' : '0 2px 8px rgba(201,168,76,0.3)',
    transition: 'transform 0.15s ease, box-shadow 0.2s ease',
    marginTop: 8,
  };

  // Sold-out overlay
  const soldOutOverlayStyle = {
    position: 'absolute',
    inset: 0,
    background: 'rgba(0,0,0,0.55)',
    backdropFilter: 'blur(2px)',
    color: '#aaa',
    fontSize: 13,
    letterSpacing: 5,
    fontFamily: 'system-ui',
    fontWeight: 800,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 4,
  };

  // Image dots
  const dotsRowStyle = {
    position: 'absolute',
    bottom: 8,
    left: 10,
    display: 'flex',
    gap: 4,
    zIndex: 2,
  };

  const dotStyle = (active) => ({
    width: 5,
    height: 5,
    borderRadius: '50%',
    background: active ? '#c9a84c' : 'rgba(255,255,255,0.4)',
    transition: 'background 0.25s ease',
    boxShadow: active ? '0 0 6px rgba(201,168,76,0.6)' : 'none',
  });

  // Info panel
  const infoStyle = {
    padding: isShowcase ? '12px 14px 14px' : '10px 12px 12px',
    background: 'linear-gradient(180deg, transparent, rgba(201,168,76,0.04))',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  };

  const topRowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 };

  const catLabelStyle = {
    color: 'rgba(201,168,76,0.65)',
    fontSize: 9,
    letterSpacing: 1.8,
    textTransform: 'uppercase',
    fontWeight: 600,
    fontFamily: 'system-ui',
  };

  const ratingStyle = {
    color: '#fbbf24',
    fontSize: 10,
    fontFamily: 'system-ui',
    whiteSpace: 'nowrap',
  };

  const nameStyle = {
    color: '#f5f0e8',
    fontSize: isShowcase ? 14 : 13,
    fontWeight: 600,
    lineHeight: 1.4,
    fontFamily: 'Georgia, "Times New Roman", serif',
    letterSpacing: '0.2px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    minHeight: isShowcase ? 38 : 36,
    margin: 0,
  };

  const priceRowStyle = {
    display: 'flex',
    alignItems: 'baseline',
    gap: 6,
    marginTop: 2,
    flexWrap: 'wrap',
  };

  const priceStyle = {
    color: '#c9a84c',
    fontWeight: 800,
    fontSize: isShowcase ? 17 : 15,
    fontFamily: 'system-ui',
    letterSpacing: 0.2,
  };

  const oldPriceStyle = {
    color: '#444458',
    fontSize: 11,
    textDecoration: 'line-through',
    fontFamily: 'system-ui',
  };

  const savingsPillStyle = {
    color: '#22c55e',
    fontSize: 9,
    fontWeight: 700,
    marginLeft: 'auto',
    background: 'rgba(34,197,94,0.15)',
    padding: '2px 7px',
    borderRadius: 999,
    fontFamily: 'system-ui',
    letterSpacing: 0.3,
    whiteSpace: 'nowrap',
  };

  return (
    <>
    <Link
      to={`/product/${p._id}`}
      style={{ textDecoration: 'none', display: 'block' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={cardStyle} className="gg-premium-card">
        {/* IMAGE AREA */}
        <div style={imgWrapStyle}>
          <div style={shimmerStyle} />
          {currentImg ? (
            <img
              src={cldCard(currentImg)}
              alt={p.name || 'product'}
              style={imgStyle}
              loading="lazy"
              draggable={false}
              onError={e => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div style={placeholderStyle}>💎</div>
          )}

          <div style={haloStyle} />

          {/* sparkles — only visible on hover for restraint */}
          {hovered && !isSoldOut && (
            <>
              <span style={{ position:'absolute', top:'18%', right:'14%', color:'rgba(255,255,255,0.7)', fontSize:11, pointerEvents:'none' }}>✦</span>
              <span style={{ position:'absolute', bottom:'30%', left:'16%', color:'rgba(255,255,255,0.55)', fontSize:9, pointerEvents:'none' }}>✧</span>
              <span style={{ position:'absolute', top:'42%', right:'22%', color:'rgba(201,168,76,0.5)', fontSize:7, pointerEvents:'none' }}>✦</span>
            </>
          )}

          <div style={bottomGradientStyle} />

          {/* Badges */}
          {discount > 0 && <div style={discountBadgeStyle}>{discount}% OFF</div>}
          {isFresh && (
            <div style={newRibbonStyle}>
              <span>✦</span>NEW
            </div>
          )}

          {/* Wishlist heart */}
          <button
            onClick={handleWishlist}
            style={wishlistStyle}
            aria-label={wished ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            {wished ? '♥' : '♡'}
          </button>

          {/* Apr28 v11 — Share with my creator code (only for approved creators).
              Apr28 v15 — Desktop-only floating button. On mobile, share moves
              to the info panel below the price to avoid crowding the small
              image area. Heart stays floating for everyone (one-tap wishlist). */}
          {creatorCode && !isMobile && (
            <button
              onClick={handleShare}
              style={shareBtnStyle}
              aria-label="Share with my creator code"
              title={hi ? 'मेरे कोड से शेयर करें' : 'Share with my code'}
            >
              🔗
            </button>
          )}

          {/* Image dots indicator (when multiple) */}
          {hasMultipleImages && !isSoldOut && (
            <div style={dotsRowStyle}>
              {p.images.slice(0, 4).map((_, i) => (
                <span key={i} style={dotStyle(imgIdx === i)} />
              ))}
            </div>
          )}

          {/* Stock urgency */}
          {isLowStock && (
            <div style={stockUrgencyStyle}>
              <span style={stockDotStyle} />
              {hi ? `केवल ${p.stock} बचे` : `Only ${p.stock} left`} ⚡
            </div>
          )}

          {/* Sold out overlay */}
          {isSoldOut && (
            <div style={soldOutOverlayStyle}>
              {hi ? 'समाप्त' : 'SOLD OUT'}
            </div>
          )}
        </div>

        {/* INFO */}
        <div style={infoStyle}>
          <div style={topRowStyle}>
            <span style={catLabelStyle}>{p.category}</span>
            {p.numReviews > 0 && (
              <span style={ratingStyle}>
                ★ {Number(p.rating || 0).toFixed(1)}{' '}
                <span style={{ color:'#666', fontWeight:400, fontSize:9 }}>({p.numReviews})</span>
              </span>
            )}
          </div>
          <h3 style={nameStyle}>{hi && p.nameHi ? p.nameHi : p.name}</h3>
          <div style={priceRowStyle}>
            <span style={priceStyle}>₹{sellPrice?.toLocaleString()}</span>
            {discount > 0 && <span style={oldPriceStyle}>₹{p.price?.toLocaleString()}</span>}
            {savings > 0 && (
              <span style={savingsPillStyle}>
                {hi ? `बचत ₹${savings.toLocaleString()}` : `Save ₹${savings.toLocaleString()}`}
              </span>
            )}
          </div>

          {/* Apr28 v8: Quick Add now sits BELOW the price row, inside info
              panel — keeps the product photo clean. Smaller, less aggressive
              than the old image overlay version. */}
          <button onClick={handleQuickAdd} style={quickAddStyle} disabled={adding || isSoldOut}>
            {isSoldOut
              ? (hi ? 'स्टॉक में नहीं' : 'Sold Out')
              : adding
                ? (hi ? '✓ जोड़ा गया' : '✓ Added')
                : (hi ? '+ कार्ट में जोड़ें' : '+ Add to Cart')}
          </button>

          {/* Apr28 v15 — Mobile-only inline share link for creators.
              On mobile, the floating 🔗 was crowding the small image area.
              Now it lives here as a subtle text link below the Add button.
              Heart stays in image area (one-tap visible for all customers).
              Desktop creators still see the floating gold button. */}
          {creatorCode && isMobile && !isSoldOut && (
            <button
              type="button"
              onClick={handleShare}
              style={mobileShareLinkStyle}
              aria-label="Share with my creator code"
            >
              🔗 {hi ? 'मेरे कोड से शेयर करें' : 'Share with my code'}
            </button>
          )}
        </div>

        {/* Animations injected once globally */}
      </div>
    </Link>
    {/* Apr28 v11 — Affiliate share modal (creators only). Sits outside <Link>
        so clicks on it don't trigger navigation. presetProduct pre-loads
        this card's product so creator skips the search step. */}
    {creatorCode && (
      <AffiliateLinkBuilder
        creatorCode={creatorCode}
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        presetProduct={p}
      />
    )}
    </>
  );
};

// One-time global animation injection
if (typeof document !== 'undefined' && !document.getElementById('gg-premium-card-anim')) {
  const style = document.createElement('style');
  style.id = 'gg-premium-card-anim';
  style.textContent = `
    @keyframes ggStockPulse {
      0%, 100% { transform: scale(1); opacity: 1; }
      50%      { transform: scale(1.4); opacity: 0.55; }
    }
  `;
  document.head.appendChild(style);
}

export default PremiumProductCard;
